import { memo, useCallback, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { PaymentIframe } from "@/components/payment/PaymentIframe";
import { useCRMPayment } from "@/hooks/useCRMPayment";

/**
 * Pacotes Fluxmed no mesmo visual minimalista dos passos de introdução/configuração:
 * linhas com borda `divider`, ícone em caixa 44px e tipografia compacta (sem Paper elevado).
 *
 * O checkout (iframe Mercado Pago) abre em **modal**; «Prosseguir» no iframe fecha o modal,
 * marca o fluxo como concluído e dispara `onContractActivated` (ex.: atualizar estatísticas FHIR).
 */
export interface CrmSetupContractFluxmedPackagesSectionProps {
  /** Chamado ao clicar em «Prosseguir» no iframe, após o pagamento (atualiza métricas de franquia). */
  onContractActivated?: () => void;
}

const CrmSetupContractFluxmedPackagesSection = memo(
  ({ onContractActivated }: CrmSetupContractFluxmedPackagesSectionProps) => {
    const {
      paymentLink,
      setPaymentLink,
      handleActivateCRMPlan,
      crmPlans,
    } = useCRMPayment();

    const [isContractActivated, setIsContractActivated] = useState(false);
    /** Só o pacote clicado mostra «Gerando...»; os outros permanecem «Continuar» (todos desabilitados durante a requisição). */
    const [loadingProductReference, setLoadingProductReference] = useState<
      string | null
    >(null);

    const handleContinuePlan = useCallback(
      async (planItem: (typeof crmPlans)[number]) => {
        setLoadingProductReference(planItem.productReference);
        try {
          await handleActivateCRMPlan(planItem);
        } finally {
          setLoadingProductReference(null);
        }
      },
      [handleActivateCRMPlan],
    );

    const formatBrl = (value: number) =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);

    /** Fecha o modal sem concluir o fluxo (Voltar / clique fora / ESC) — não atualiza estatísticas. */
    const handleCloseCheckoutModal = useCallback(() => {
      setPaymentLink(null);
    }, [setPaymentLink]);

    /**
     * Botão «Prosseguir» do `PaymentIframe`: fecha o modal e notifica o pai para recarregar
     * disponíveis / enviadas / contratadas (mesmo momento em que o legado chama `fetchAllCRMData`).
     */
    const handleCheckoutProceed = useCallback(() => {
      setPaymentLink(null);
      setIsContractActivated(true);
      onContractActivated?.();
    }, [setPaymentLink, onContractActivated]);

    return (
      <>
        <Dialog
          open={Boolean(paymentLink)}
          onClose={handleCloseCheckoutModal}
          fullWidth
          maxWidth={false}
          scroll="paper"
          aria-labelledby="crm-fluxmed-checkout-dialog-title"
          /**
           * O `Paper` do Dialog vem com `overflowY: 'auto'` no tema MUI — isso gerava scroll
           * no próprio modal. Passamos `PaperProps` para forçar `hidden` no papel do diálogo.
           */
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: { xs: "100vw", sm: "min(1024px, calc(100vw - 32px))" },
              height: { xs: "100%", sm: "min(92dvh, 920px)" },
              maxHeight: { xs: "100dvh", sm: "92dvh" },
              margin: { xs: 0, sm: 2 },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              overflowX: "hidden",
              overflowY: "hidden",
              boxSizing: "border-box",
            },
          }}
          sx={{
            "& .MuiDialog-container": {
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            id="crm-fluxmed-checkout-dialog-title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              py: 1.25,
              pr: 1,
              flexShrink: 0,
              flex: "0 0 auto",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} component="span">
              Pagamento
            </Typography>
            <IconButton
              type="button"
              aria-label="Fechar pagamento"
              onClick={handleCloseCheckoutModal}
              edge="end"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              /** DialogContent também define `overflow-y: auto` no MUI — precisa vencer a especificidade. */
              overflow: "hidden !important",
              overflowX: "hidden !important",
              overflowY: "hidden !important",
            }}
          >
            {paymentLink ? (
              <Box
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  minWidth: 0,
                  maxWidth: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  overscrollBehavior: "contain",
                }}
              >
                <PaymentIframe
                  showBackButton={false}
                  paymentLink={paymentLink}
                  onBack={handleCheckoutProceed}
                />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>

        <Stack spacing={1.5}>
          {isContractActivated ? (
            <Typography variant="body2" color="success.main" fontWeight={700}>
              Pacote contratado! Você pode finalizar.
            </Typography>
          ) : (
            <Typography variant="subtitle2" color="text.secondary">
              Escolha um pacote de mensagens para o relacionamento com pacientes.
            </Typography>
          )}

          <Stack spacing={1.25}>
            {crmPlans.map((planItem) => (
              <Stack
                key={planItem.productReference}
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.default",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "success100.main",
                    border: "1px solid",
                    borderColor: "success200.main",
                    flexShrink: 0,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 22, color: "success600.main" }} />
                </Box>

                <Stack spacing={0.35} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {planItem.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {planItem.description}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ pt: 0.25 }}>
                    {formatBrl(planItem.amount)}
                  </Typography>
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleContinuePlan(planItem)}
                  sx={{
                    alignSelf: { xs: "stretch", sm: "center" },
                    flexShrink: 0,
                    minWidth: { sm: 120 },
                  }}
                  disabled={loadingProductReference !== null}
                >
                  {loadingProductReference === planItem.productReference
                    ? "Gerando..."
                    : "Continuar"}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </>
    );
  },
);

CrmSetupContractFluxmedPackagesSection.displayName =
  "CrmSetupContractFluxmedPackagesSection";

export default CrmSetupContractFluxmedPackagesSection;
